import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import { Nullable, UserWithToken, WithData } from '@ygoargentina/sdk/api/api.types';
import axiosClient from './request-client';
import { AxiosRequestConfig } from 'axios';

// Types
export type StreamingElement = {
  id: string;
  bind: string;
  format?: string;
  x: number;
  y: number;
  fontSize: number;
  color: string;
  fontFamily: string;
  bold: boolean;
  shadow: boolean;
  visible: boolean;
  width?: number;
  height?: number;
  textAlign?: string;
  overflow?: 'wrap' | 'ellipsis';
  /**
   * `camera`: dónde va la cámara de la mesa (no dibuja nada). El celular, que
   * quema el overlay en el video, encaja ahí la cámara en vez de ponerla de
   * fondo a pantalla completa.
   *
   * `card`: la carta destacada que elige el operador en el panel de control
   * (a mano o tocando una sugerencia de la detección de cartas). Que la
   * escena lo tenga es lo que prende la detección en esa transmisión.
   */
  type?: 'text' | 'image' | 'pairings' | 'standings' | 'bracket' | 'camera' | 'card';
  textTransform?: 'none' | 'uppercase';
  letterSpacing?: number;
  staticText?: string;
  marquee?: boolean;
  /** px/s, default 80 */
  marqueeSpeed?: number;
  /** Texto: lista de mensajes (ticker o rotación por reloj) */
  messages?: string[];
  /** Separador entre mensajes del ticker, default ' • ' */
  messageSeparator?: string;
  /** Segundos por mensaje sin ticker, default 8 */
  messageSeconds?: number;
  shape?: 'square' | 'rect';
  /** Carta: segundos que queda en pantalla, default 8; 0 = hasta que la saquen */
  cardSeconds?: number;
  /** Imagen: de dónde sale, default deckCover */
  imageSource?: 'deckCover' | 'upload' | 'organizerLogo';
  /** Imagen subida (URL completa) */
  imageUrl?: string;
  /** Imagen: ajuste; default contain para upload/organizerLogo */
  objectFit?: 'contain' | 'cover';
  borderRadius?: number;
  borderWidth?: number;
  borderColor?: string;
  /** Pareos: columnas (default 1) */
  columns?: 1 | 2;
  /** Pareos: alto de cada fila, default 64 */
  rowHeight?: number;
  /** Pareos: separación entre filas, default 12 */
  rowGap?: number;
  rowBackground?: string;
  rowBorderColor?: string;
  tableBadgeColor?: string;
  tableBadgeTextColor?: string;
  /** Pareos: color del ganador, default #F5C542 */
  winnerColor?: string;
  /** Pareos: default full */
  nameFormat?: 'full' | 'short';
  /** Pareos y standings: segundos por página, default 12 */
  pageSeconds?: number;
  /** Standings: filas por página, default 8 */
  rowsPerPage?: number;
  /** Standings: columna DDD, default false */
  showDdd?: boolean;
  /** Standings: fila de encabezado, default true */
  showHeader?: boolean;
  /** Standings: color del encabezado, default el del recuadro de puesto */
  headerColor?: string;
  /** Bracket: nombre de cada ronda arriba de su columna, default true */
  showRoundNames?: boolean;
  /** Bracket: seed del top (1..N) antes del nombre, default true */
  showSeeds?: boolean;
  /** Bracket: texto del perdedor y de los slots vacíos, default #6B7280 */
  mutedColor?: string;
  /** Bracket: líneas entre una mesa y la que alimenta, default #C9A227 */
  lineColor?: string;
};

export type StreamingScene = {
  id: number;
  organizerId: number;
  name: string;
  slug: string;
  apiUrl?: string;
  refreshInterval: number;
  canvasWidth: number;
  canvasHeight: number;
  backgroundImage?: string;
  brbScene?: string;
  preStartScene?: string;
  breakScene?: string;
  topBreakScene?: string;
  winIcon?: string;
  lossIcon?: string;
  organizerLogoUrl?: string | null;
  elements: StreamingElement[];
  createdAt: string;
  updatedAt: string;
};

export type StreamingPlayer = {
  name?: string;
  secondName?: string | null;
  lastName?: string;
  secondLastName?: string | null;
  fullName?: string;
  shortFullName?: string;
  longFullName?: string;
  shortName?: string;
  nickname?: string;
  deck?: string;
  deckCoverSquare?: string | null;
  deckCoverRect?: string | null;
  score?: string;
  wins?: number[];
  winCount?: number;
  lifePoints?: number;
  userId?: number | null;
  tournamentPlayerId?: number | null;
  metaDeckId?: number | null;
};

export type StreamingTimer = {
  startedAt?: string;
  duration?: number;
  remaining?: number;
};

export type StreamingPairingPlayer = {
  fullName: string;
  shortName: string;
};

export type StreamingPairing = {
  table: number;
  playerA?: StreamingPairingPlayer | null;
  playerB?: StreamingPairingPlayer | null;
  finished: boolean;
  winner?: string | null;
  /** WIN, DRAW, DOUBLE_LOSS o BYE; null mientras se juega (o API viejo). */
  outcome?: string | null;
};

export type StreamingStanding = {
  rank: number;
  player: StreamingPairingPlayer;
  points: number;
  wins: number;
  losses: number;
  draws: number;
  /** 0-100 */
  omw: number;
  oomw: number;
  ddd: number;
  tiebreaker: string;
  dropped: boolean;
};

export type StreamingBracketPlayer = StreamingPairingPlayer & {
  /** Seed de la ronda 1 del top (1..N); null sin suizo vinculado */
  seed?: number | null;
};

export type StreamingBracketMatch = {
  table: number;
  playerA?: StreamingBracketPlayer | null;
  playerB?: StreamingBracketPlayer | null;
  bye: boolean;
  finished: boolean;
  winner?: string | null;
};

export type StreamingBracketRound = {
  round: number;
  name: string;
  matches: StreamingBracketMatch[];
};

export type StreamingData = {
  tournament?: {
    id: number;
    title: string;
    currentRound: number;
    rounds: number;
  };
  streaming: boolean;
  playerA?: StreamingPlayer;
  playerB?: StreamingPlayer;
  round?: number;
  /** «Ronda 3» en un suizo; «Top 8», «Top 4» o «Final» en un top. */
  roundName?: string | null;
  table?: number;
  timer?: StreamingTimer;
  organizerSlug?: string | null;
  sceneSlug?: string | null;
  /** Por mesa: dueño de la escena (la compartida es de YGO Argentina). */
  sceneOrganizerSlug?: string | null;
  /** Por mesa: logo del organizador del torneo (gana sobre el de la escena). */
  organizerLogoUrl?: string | null;
  /** Logos de todos los organizadores del torneo (el principal primero); con más de uno rotan. */
  organizerLogoUrls?: string[] | null;
  formatName?: string | null;
  tournamentDate?: string | null;
  pairings?: StreamingPairing[] | null;
  standings?: StreamingStanding[] | null;
  /** Top de eliminación directa al aire; [] con un suizo */
  bracket?: StreamingBracketRound[] | null;
  /** Double elimination: llave de losers (con `bracket` = winners); [] en los demás. */
  bracketLosers?: StreamingBracketRound[] | null;
  /** Double elimination: gran final y revancha; [] en los demás. */
  bracketGrandFinal?: StreamingBracketRound[] | null;
  /** Mesa por el 3er puesto, aparte del bracket; null si no se juega. */
  bracketThirdPlace?: StreamingBracketMatch | null;
  phase?: string | null;
  /** Panel de control: escena forzada por el operador (pisa la de la fase). */
  forcedSceneSlug?: string | null;
  /** Panel de control: overrides de elementos por clave (`{ text?, imageUrl? }`). */
  elementOverrides?: Record<string, { text?: string | null; imageUrl?: string | null }> | null;
  /**
   * Panel de control: campos pisados que en el schema no son texto (round,
   * table, tournament.currentRound, tournament.rounds, tournamentDate), por
   * ruta de binding. El overlay los muestra tal cual.
   */
  fieldOverrides?: Record<string, string> | null;
  /** Panel de control: la carta del elemento «carta»; `shownAt` (ISO) es desde cuándo. */
  featuredCard?: StreamFeaturedCard | null;
  matchId?: number | null;
};

export type StreamFeaturedCard = {
  konamiId: number;
  name: string;
  imageUrl: string;
  shownAt: string;
};

export type StreamingSceneInput = {
  name: string;
  slug?: string;
  apiUrl?: string;
  refreshInterval?: number;
  canvasWidth?: number;
  canvasHeight?: number;
  brbScene?: string;
  preStartScene?: string;
  breakScene?: string;
  topBreakScene?: string;
  elements?: StreamingElement[];
};

/** Escena para elegir en una transmisión por mesa. */
export type StreamingSceneOption = { slug: string; name: string };

/**
 * Slug del organizador principal de un torneo, con la forma que trae
 * `getTournament` (`organizers`). Sin él no se pueden listar sus escenas.
 */
export function primaryOrganizerSlug(tournament: {
  organizerId?: number | null;
  organizers?: Array<{
    organizerId?: number | null;
    isPrimary?: boolean | null;
    organizer?: { slug?: string | null } | null;
  } | null> | null;
} | null | undefined): string | null {
  const list = (tournament?.organizers ?? []).filter(Boolean);
  const main =
    list.find((o) => o!.organizerId === tournament?.organizerId) ??
    list.find((o) => o!.isPrimary) ??
    list[0];
  return main?.organizer?.slug ?? null;
}

// GraphQL Queries
/** Las escenas del organizador, sólo slug y nombre (el select de la transmisión). */
export const listStreamingSceneOptions = async (
  user: Nullable<UserWithToken>,
  organizerSlug: string
): Promise<StreamingSceneOption[]> =>
  (
    await getGraphQLQueryClient(user).request<{
      data: { data: StreamingSceneOption[] };
    }>(
      gql`
        query streamingSceneOptions($organizerSlug: String!) {
          data: streamingScenes(organizerSlug: $organizerSlug) {
            data {
              slug
              name
            }
          }
        }
      `,
      { organizerSlug }
    )
  ).data.data;

export const listStreamingScenes = async (
  user: Nullable<UserWithToken>,
  organizerSlug: string
) =>
  getGraphQLQueryClient(user).request<{ data: { data: StreamingScene[] } }>(
    gql`
      query streamingScenes($organizerSlug: String!) {
        data: streamingScenes(organizerSlug: $organizerSlug) {
          data {
            id
            organizerId
            name
            slug
            apiUrl
            refreshInterval
            canvasWidth
            canvasHeight
            backgroundImage
            brbScene
            preStartScene
            breakScene
            topBreakScene
            winIcon
            lossIcon
            elements
            createdAt
            updatedAt
          }
        }
      }
    `,
    { organizerSlug }
  );

/** De dónde sale la escena de la URL fija de OBS de una tienda. */
export type StreamingOverlayScene = {
  sceneOrganizerSlug: string;
  sceneSlug: string;
  organizerLogoUrl?: string | null;
};

export const getStreamingOverlayScene = async (
  user: Nullable<UserWithToken>,
  organizerSlug: string
) =>
  getGraphQLQueryClient(user).request<WithData<StreamingOverlayScene>>(
    gql`
      query publicStreamingOverlayScene($organizerSlug: String!) {
        data: publicStreamingOverlayScene(organizerSlug: $organizerSlug) {
          sceneOrganizerSlug
          sceneSlug
          organizerLogoUrl
        }
      }
    `,
    { organizerSlug }
  );

export const getStreamingScene = async (
  user: Nullable<UserWithToken>,
  organizerSlug: string,
  sceneSlug: string
) =>
  getGraphQLQueryClient(user).request<WithData<StreamingScene>>(
    gql`
      query streamingScene($organizerSlug: String!, $sceneSlug: String!) {
        data: streamingScene(organizerSlug: $organizerSlug, sceneSlug: $sceneSlug) {
          id
          organizerId
          name
          slug
          apiUrl
          refreshInterval
          canvasWidth
          canvasHeight
          backgroundImage
          brbScene
          preStartScene
          breakScene
          topBreakScene
          winIcon
          lossIcon
          organizerLogoUrl
          elements
          createdAt
          updatedAt
        }
      }
    `,
    { organizerSlug, sceneSlug }
  );

export const getStreamingData = async (
  user: Nullable<UserWithToken>,
  organizerSlug: string
) =>
  getGraphQLQueryClient(user).request<WithData<StreamingData>>(
    gql`
      query streamingData($organizerSlug: String!) {
        data: streamingData(organizerSlug: $organizerSlug) {
          tournament {
            id
            title
            currentRound
            rounds
          }
          streaming
          playerA {
            name
            secondName
            lastName
            secondLastName
            fullName
            shortFullName
            longFullName
            shortName
            nickname
            deck
            deckCoverSquare
            deckCoverRect
            score
            wins
            winCount
            lifePoints
            userId
            tournamentPlayerId
            metaDeckId
          }
          playerB {
            name
            secondName
            lastName
            secondLastName
            fullName
            shortFullName
            longFullName
            shortName
            nickname
            deck
            deckCoverSquare
            deckCoverRect
            score
            wins
            winCount
            lifePoints
            userId
            tournamentPlayerId
            metaDeckId
          }
          round
          roundName
          table
          timer {
            startedAt
            duration
            remaining
          }
          formatName
          tournamentDate
          organizerLogoUrls
          phase
          forcedSceneSlug
          elementOverrides
          fieldOverrides
          featuredCard
          matchId
          pairings {
            table
            finished
            winner
            outcome
            playerA {
              fullName
              shortName
            }
            playerB {
              fullName
              shortName
            }
          }
          standings {
            rank
            player {
              fullName
              shortName
            }
            points
            wins
            losses
            draws
            omw
            oomw
            ddd
            tiebreaker
            dropped
          }
          bracket {
            round
            name
            matches {
              table
              bye
              finished
              winner
              playerA {
                fullName
                shortName
                seed
              }
              playerB {
                fullName
                shortName
                seed
              }
            }
          }
          bracketLosers {
            round
            name
            matches {
              table
              bye
              finished
              winner
              playerA {
                fullName
                shortName
                seed
              }
              playerB {
                fullName
                shortName
                seed
              }
            }
          }
          bracketGrandFinal {
            round
            name
            matches {
              table
              bye
              finished
              winner
              playerA {
                fullName
                shortName
                seed
              }
              playerB {
                fullName
                shortName
                seed
              }
            }
          }
          bracketThirdPlace {
            table
            bye
            finished
            winner
            playerA {
              fullName
              shortName
              seed
            }
            playerB {
              fullName
              shortName
              seed
            }
          }
        }
      }
    `,
    { organizerSlug }
  );

// GraphQL Mutations
export const createStreamingScene = async (
  user: Nullable<UserWithToken>,
  organizerSlug: string,
  input: StreamingSceneInput
) =>
  getGraphQLQueryClient(user).request<WithData<StreamingScene>>(
    gql`
      mutation createStreamingScene($organizerSlug: String!, $input: StreamingSceneInput!) {
        data: createStreamingScene(organizerSlug: $organizerSlug, input: $input) {
          id
          organizerId
          name
          slug
          apiUrl
          refreshInterval
          canvasWidth
          canvasHeight
          backgroundImage
          brbScene
          preStartScene
          breakScene
          topBreakScene
          winIcon
          lossIcon
          elements
          createdAt
          updatedAt
        }
      }
    `,
    { organizerSlug, input }
  );

export const updateStreamingScene = async (
  user: Nullable<UserWithToken>,
  organizerSlug: string,
  sceneSlug: string,
  input: StreamingSceneInput
) =>
  getGraphQLQueryClient(user).request<WithData<StreamingScene>>(
    gql`
      mutation updateStreamingScene(
        $organizerSlug: String!
        $sceneSlug: String!
        $input: StreamingSceneInput!
      ) {
        data: updateStreamingScene(
          organizerSlug: $organizerSlug
          sceneSlug: $sceneSlug
          input: $input
        ) {
          id
          organizerId
          name
          slug
          apiUrl
          refreshInterval
          canvasWidth
          canvasHeight
          backgroundImage
          brbScene
          preStartScene
          breakScene
          topBreakScene
          winIcon
          lossIcon
          elements
          createdAt
          updatedAt
        }
      }
    `,
    { organizerSlug, sceneSlug, input }
  );

export const duplicateStreamingScene = async (
  user: Nullable<UserWithToken>,
  organizerSlug: string,
  sceneSlug: string,
  newName: string
) =>
  getGraphQLQueryClient(user).request<WithData<StreamingScene>>(
    gql`
      mutation duplicateStreamingScene(
        $organizerSlug: String!
        $sceneSlug: String!
        $newName: String!
      ) {
        data: duplicateStreamingScene(
          organizerSlug: $organizerSlug
          sceneSlug: $sceneSlug
          newName: $newName
        ) {
          id
          organizerId
          name
          slug
          apiUrl
          refreshInterval
          canvasWidth
          canvasHeight
          backgroundImage
          brbScene
          preStartScene
          breakScene
          topBreakScene
          winIcon
          lossIcon
          elements
          createdAt
          updatedAt
        }
      }
    `,
    { organizerSlug, sceneSlug, newName }
  );

export const deleteStreamingScene = async (
  user: Nullable<UserWithToken>,
  organizerSlug: string,
  sceneSlug: string
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deleteStreamingScene($organizerSlug: String!, $sceneSlug: String!) {
        data: deleteStreamingScene(organizerSlug: $organizerSlug, sceneSlug: $sceneSlug)
      }
    `,
    { organizerSlug, sceneSlug }
  );

// REST endpoints for file uploads (multipart/form-data)
export const uploadSceneBackground = (
  organizerSlug: string,
  sceneSlug: string,
  file: File,
  config?: AxiosRequestConfig
) => {
  const formData = new FormData();
  formData.append('background', file);

  return axiosClient.post<{ backgroundImage: string }>(
    `/streaming/${organizerSlug}/scenes/${sceneSlug}/background`,
    formData,
    {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': undefined,
      },
    }
  );
};

export const uploadSceneImage = (
  organizerSlug: string,
  sceneSlug: string,
  file: File,
  config?: AxiosRequestConfig
) => {
  const formData = new FormData();
  formData.append('image', file);

  return axiosClient.post<{ imageUrl: string }>(
    `/streaming/${organizerSlug}/scenes/${sceneSlug}/images`,
    formData,
    {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': undefined,
      },
    }
  );
};

export const deleteSceneBackground = (
  organizerSlug: string,
  sceneSlug: string,
  config?: AxiosRequestConfig
) => {
  return axiosClient.delete(`/streaming/${organizerSlug}/scenes/${sceneSlug}/background`, config);
};

export const uploadSceneWinIcon = (
  organizerSlug: string,
  sceneSlug: string,
  file: File,
  config?: AxiosRequestConfig
) => {
  const formData = new FormData();
  formData.append('icon', file);

  return axiosClient.post<{ winIcon: string }>(
    `/streaming/${organizerSlug}/scenes/${sceneSlug}/win-icon`,
    formData,
    {
      ...config,
      headers: { ...config?.headers, 'Content-Type': undefined },
    }
  );
};

export const deleteSceneWinIcon = (
  organizerSlug: string,
  sceneSlug: string,
  config?: AxiosRequestConfig
) => {
  return axiosClient.delete(`/streaming/${organizerSlug}/scenes/${sceneSlug}/win-icon`, config);
};

export const uploadSceneLossIcon = (
  organizerSlug: string,
  sceneSlug: string,
  file: File,
  config?: AxiosRequestConfig
) => {
  const formData = new FormData();
  formData.append('icon', file);

  return axiosClient.post<{ lossIcon: string }>(
    `/streaming/${organizerSlug}/scenes/${sceneSlug}/loss-icon`,
    formData,
    {
      ...config,
      headers: { ...config?.headers, 'Content-Type': undefined },
    }
  );
};

export const deleteSceneLossIcon = (
  organizerSlug: string,
  sceneSlug: string,
  config?: AxiosRequestConfig
) => {
  return axiosClient.delete(`/streaming/${organizerSlug}/scenes/${sceneSlug}/loss-icon`, config);
};
